import { createSelector } from '@reduxjs/toolkit';

import type { FilesFileSchemaWithId } from '@iconik-io/iconik-api/src/services/files';
import { selectUploaderStateList } from '@iconik-io/shared-lib/src/uploader/states_slice';
import {
  UPLOADER_STATUS_COMPLETED,
  UPLOADER_STATUS_UPLOADING,
  type UploaderState,
} from '@iconik-io/uploader/src/state';

import type { AppState } from '../../store/types';
import type { UploadItem } from './types';

export const selectUploads = (state: AppState): UploaderState[] =>
  selectUploaderStateList(state);

export const selectUpload = (
  state: AppState,
  stateId: string,
): UploaderState | undefined => state.uploaderStates[stateId];

export const selectUploadItem = (
  state: AppState,
  stateId: string,
): UploadItem | undefined => state.uploaderItems[stateId];

export const getIsCollectionsCreation = (state: AppState): boolean =>
  state.uploaderReducer.isCollectionsCreation;

export const selectAllUploadsFinished = createSelector(
  [selectUploads],
  (uploads) =>
    uploads.every((upload) => upload.status === UPLOADER_STATUS_COMPLETED),
);

export const selectUploadInProgress = createSelector(
  [selectUploads, getIsCollectionsCreation],
  (uploads, isCollectionsCreation) =>
    uploads.some((upload) => upload.status === UPLOADER_STATUS_UPLOADING) ||
    isCollectionsCreation,
);

export const selectSomeUploadFinished = createSelector(
  [selectUploads],
  (uploads) =>
    uploads.some((upload) => upload.status === UPLOADER_STATUS_COMPLETED),
);

const getAssetId = (_state: AppState, assetId: string) => assetId;
const getVersionId = (_state: AppState, _assetId: string, versionId?: string) =>
  versionId;

const getAssets = (state: AppState) => state.entities.assets;
const getFiles = (state: AppState) => state.entities.files;
const getFormats = (state: AppState) => state.entities.formats;

export const selectSourceVersionAssetFiles = createSelector(
  [getAssetId, getVersionId, getAssets, getFiles, getFormats],
  (assetId, versionId, assets, files, formats) => {
    let assetFiles: FilesFileSchemaWithId[] = [];
    if (assetId) {
      const sourceVersionId = versionId ?? assets[assetId].versions?.[0]?.id;
      if (!sourceVersionId) {
        return assetFiles;
      }
      const assetOriginalFormatId = Object.values(formats).find(
        (format) =>
          format.name === 'ORIGINAL' && format.version_id === sourceVersionId,
      )?.id;
      if (!assetOriginalFormatId) {
        return assetFiles;
      }
      assetFiles = Object.values(files).filter(
        (file) =>
          file.asset_id === assetId &&
          file.version_id === sourceVersionId &&
          file.format_id === assetOriginalFormatId,
      );
    }
    return assetFiles;
  },
);
